import React, { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { addNotice } from "../../store/slices/notice";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { uploadImage } from "../../utils/helpers/fileupload";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";

const requiredMessages = {
  usertype: "Please select user type",
  date: "Please select notice date",
  description: "Please enter description",
};

export default function AddNotice() {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);

  const [formData, setFormData] = useState({
    usertype: "",
    date: "",
    description: "",
    status: 1,
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],

    content: "",

    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      setFormData((prev) => ({
        ...prev,
        description: html,
      }));

      setErrors((prev) => ({
        ...prev,
        description: "",
      }));
    },
  });

  // ---------------- handlers ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  //file validate
  const validateFiles = () => {


    if (files.length > 5) {
      setErrors((prev) => ({ ...prev, files: "Maximum 5 files allowed" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, files: "" }));
    return true;
  };

  //  submit <----------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    Object.keys(requiredMessages).forEach((key) => {
      if (!formData[key]) {
        newErrors[key] = requiredMessages[key];
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    if (!validateFiles()) return;

    try {
      let fileUrls = [];

      for (const file of files) {
        const url = await uploadImage(file, token);
        if (url) fileUrls.push(url);
      }

      const payload = {
        usertype: Number(formData.usertype),
        date: formData.date,
        description: formData.description,
        files: fileUrls,
        status: 1,
      };

      await dispatch(addNotice(payload)).unwrap();
      toast.success("Notice added successfully");
      navigate("/institute/notices");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to add notice",
      );
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/institute/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/institute/notices">Notice</Link>

            <FiChevronRight className="separator" />

            <span className="active">Add Notice</span>
          </div>

        </div>
      </div>
      <div className="acs-card">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            {/* User Type */}
            <Col lg={3} md={6} sm={12}>
              <Form.Group>
                <Form.Label>User Type</Form.Label>
                <Form.Select
                  name="usertype"
                  value={formData.usertype}
                  onChange={handleChange}
                >
                  <option value="">Select User Type</option>
                  <option value="1">Student</option>
                  <option value="2">Staff</option>
                  <option value="3">All</option>
                </Form.Select>
                {errors.usertype && (
                  <small className="text-danger">{errors.usertype}</small>
                )}
              </Form.Group>
            </Col>

            {/* Date */}
            <Col lg={3} md={6} sm={12}>
              <Form.Group>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={handleChange}
                />
                {errors.date && (
                  <small className="text-danger">{errors.date}</small>
                )}
              </Form.Group>
            </Col>

            {/* Description */}
            <Col lg={4} md={6} sm={12}></Col>
            <Col lg={12} md={6} sm={12}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <div className="border rounded">
                  <div
                    className="d-flex flex-wrap gap-2 p-2 border-bottom"
                    style={{ background: "#f8f9fa" }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                    >
                      <b>B</b>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                    >
                      <i>I</i>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => editor?.chain().focus().toggleUnderline().run()}
                    >
                      <u>U</u>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().toggleBulletList().run()
                      }
                    >
                      • List
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().toggleOrderedList().run()
                      }
                    >
                      1. List
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign("left").run()
                      }
                    >
                      Left
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign("center").run()
                      }
                    >
                      Center
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign("right").run()
                      }
                    >
                      Right
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().undo().run()
                      }
                    >
                      Undo
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().redo().run()
                      }
                    >
                      Redo
                    </button>
                  </div>
                  <EditorContent
                    editor={editor}
                    className="notice-editor"
                  />
                </div>

                {errors.description && (
                  <small className="text-danger">{errors.description}</small>
                )}
              </Form.Group>
            </Col>

            {/* PDF */}
            <Col md={6}>
              <Form.Group>
                <Form.Label>Attachment (Max 5 files)</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files))}
                />
              </Form.Group>
            </Col>

            {errors.files && (
              <Col md={12}>
                <small className="text-danger">{errors.files}</small>
              </Col>
            )}
          </Row>

          {/* Actions */}
          <div className="an-actions mt-5">
            <button
              className="btn-cancel"
              style={{ color: "black" }}
              type="button"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button className="btn-submit" type="submit">
              Submit
            </button>
          </div>
        </Form>
      </div>
    </>
  );
}
